import * as React from "react";
import { useMemo, useState, useEffect, useCallback, createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  FormServices,
  FormRequest,
  PlayerCardServices,
  FanServices,
  Media,
  ReviewServices,
  Fan,
  Form,
} from "@/services/bedrock";
import { usePaginatedCombobox, PaginatedCombobox } from "@/utils";
import { FanCreationSchemaType } from "@/components/Forms/FanCreationForm";

interface OptionType {
  label: string;
  value: string;
}

interface FanOption {
  id: string | number;
  label: string;
  original?: Fan;
}

interface ShareFormLinkContextType {
  // Props
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playerId: number;
  formRequestId?: number;
  initialShowCustom: boolean;

  // General Loading & Status
  isLoadingDefault: boolean;
  isGenerating: boolean;
  showLoader: boolean;
  isCustomLink: boolean;
  isAiRunning: boolean;
  reviewLinkUrl: string;
  qrLogoSrc?: string;

  // Form Request States
  selectedRequest: FormRequest | null;
  setSelectedRequest: React.Dispatch<React.SetStateAction<FormRequest | null>>;
  defaultRequest: FormRequest | null;
  selectedFormId: string;
  setSelectedFormId: React.Dispatch<React.SetStateAction<string>>;
  selectedForm: Form | undefined;
  allowsAi: boolean;

  // Forms List
  formsList: Form[];
  isLoadingForms: boolean;
  formSelectItems: OptionType[];

  // Wizard Flow States
  showCustomWorkflow: boolean;
  setShowCustomWorkflow: React.Dispatch<React.SetStateAction<boolean>>;
  isEditingRequest: boolean;
  setIsEditingRequest: React.Dispatch<React.SetStateAction<boolean>>;
  wizardStep: number;
  setWizardStep: React.Dispatch<React.SetStateAction<number>>;

  // Fan Options
  createdFan: FanOption | null;
  setCreatedFan: React.Dispatch<React.SetStateAction<FanOption | null>>;
  fans: PaginatedCombobox<FanOption, false>;
  activeFanSelected: FanOption | null;
  isCreateFanOpen: boolean;
  setIsCreateFanOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCreatingFan: boolean;

  // Help Modal State
  isHelpOpen: boolean;
  setIsHelpOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // AI Customization
  useAiPersonalization: boolean;
  setUseAiPersonalization: React.Dispatch<React.SetStateAction<boolean>>;
  contextInstructions: string;
  setContextInstructions: React.Dispatch<React.SetStateAction<string>>;

  // Video Outreach Flow
  showOutreachScriptDialog: boolean;
  setShowOutreachScriptDialog: React.Dispatch<React.SetStateAction<boolean>>;
  isGeneratingOutreachScript: boolean;
  outreachScriptText: string;
  setOutreachScriptText: React.Dispatch<React.SetStateAction<string>>;
  showRecordDialog: boolean;
  setShowRecordDialog: React.Dispatch<React.SetStateAction<boolean>>;
  recordedMedia: Media | null;
  setRecordedMedia: React.Dispatch<React.SetStateAction<Media | null>>;
  showVideoPageModal: boolean;
  setShowVideoPageModal: React.Dispatch<React.SetStateAction<boolean>>;

  // API Handlers & Callbacks
  handleGenerateLink: () => Promise<FormRequest | null>;
  handleUpdateLink: () => Promise<FormRequest | null>;
  handleOpenVideoRequestWorkflow: () => Promise<void>;
  handleContinueToRecording: (script: string) => void;
  handleVideoRecordingDone: (media: Media) => void;
  handleQuickCreateFan: (name: string) => Promise<void>;
  handleEnterEditMode: () => void;
  handleCancelCustomWorkflow: () => void;
  handleResetToDefault: () => void;
  handleFanCreated: (formData: FanCreationSchemaType) => Promise<void>;
  handleNextStepFromFanSelection: () => Promise<boolean>;
  initialFormId?: string | number;
}

const ShareFormLinkContext = createContext<ShareFormLinkContextType | undefined>(undefined);

export const useShareFormLink = () => {
  const context = useContext(ShareFormLinkContext);
  if (!context) {
    throw new Error("useShareFormLink must be used within a ShareFormLinkProvider");
  }
  return context;
};

interface ShareFormLinkProviderProps {
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playerId: number;
  formRequestId?: number;
  initialShowCustom?: boolean;
  initialFormId?: string | number;
  initialFan?: Fan;
  initialWizardStep?: number;
}

export const ShareFormLinkProvider: React.FC<ShareFormLinkProviderProps> = ({
  children,
  open,
  onOpenChange,
  playerId,
  formRequestId,
  initialShowCustom = false,
  initialFormId,
  initialFan,
  initialWizardStep,
}) => {
  const [isLoadingDefault, setIsLoadingDefault] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const [selectedRequest, setSelectedRequest] = useState<FormRequest | null>(null);
  const [defaultRequest, setDefaultRequest] = useState<FormRequest | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string>(initialFormId ? String(initialFormId) : "");

  // Advanced Workflow States
  const [showCustomWorkflow, setShowCustomWorkflow] = useState<boolean>(false);
  const [isEditingRequest, setIsEditingRequest] = useState<boolean>(false);
  const [wizardStep, setWizardStep] = useState<number>(initialWizardStep || 1);
  const [isCreateFanOpen, setIsCreateFanOpen] = useState<boolean>(false);
  const [isCreatingFan, setIsCreatingFan] = useState<boolean>(false);
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);
  const [useAiPersonalization, setUseAiPersonalization] = useState<boolean>(false);
  const [contextInstructions, setContextInstructions] = useState<string>("");
  const [createdFan, setCreatedFan] = useState<FanOption | null>(
    initialFan
      ? {
          id: initialFan.id,
          label: `${initialFan.displayName || "No Name"}${initialFan.email ? ` - ${initialFan.email}` : ""}`,
          original: initialFan,
        }
      : null
  );

  // New Video Review Request Outreach States
  const [showOutreachScriptDialog, setShowOutreachScriptDialog] = useState(false);
  const [isGeneratingOutreachScript, setIsGeneratingOutreachScript] = useState(false);
  const [outreachScriptText, setOutreachScriptText] = useState("");
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [recordedMedia, setRecordedMedia] = useState<Media | null>(null);
  const [showVideoPageModal, setShowVideoPageModal] = useState(false);

  // 1. Fetch available active forms to select from
  const { data: formsData, isLoading: isLoadingForms } = useQuery({
    queryKey: ["forms", { page: 1, perPage: 100 }],
    queryFn: () => FormServices.getForms({ page: 1, perPage: 100, active: true }),
    enabled: open,
  });
  const formsList = formsData?.content || [];

  const formSelectItems = useMemo<OptionType[]>(() => {
    return formsList.map(form => ({
      label: form.displayName,
      value: String(form.id),
    }));
  }, [formsList]);

  const selectedForm = useMemo(() => {
    return formsList.find(f => String(f.id) === selectedFormId);
  }, [formsList, selectedFormId]);

  const allowsAi = selectedForm ? selectedForm.allowAiPersonalization : false;

  // 2. Fetch Player's main card to get their avatar as QR logo
  const { data: playerCard } = useQuery({
    queryKey: ["mainPlayerCard", playerId],
    queryFn: () => PlayerCardServices.getMainPlayerCard(playerId),
    enabled: !!playerId && open,
  });
  const qrLogoSrc = playerCard?.player?.photoUrl;

  // 3. Paginated Fans Combobox filtered by playerId
  const getFans = useCallback(
    async (search: string | undefined, page: number) => {
      const result = await FanServices.getFans({
        search,
        page,
        active: true,
        perPage: 25,
        playerId,
      } as any);
      return {
        ...result,
        content: result.content.map(fan => ({
          id: fan.id,
          label: `${fan.displayName || "No Name"}${fan.email ? ` - ${fan.email}` : ""}`,
          original: fan,
        })),
      };
    },
    [playerId]
  );

  const fans = usePaginatedCombobox(getFans, false, null, query => ({
    id: `temp-${Date.now()}`,
    label: query,
    original: { displayName: query } as any,
  }));

  const activeFanSelected = createdFan || fans.value;

  // 4. Load Player's Form Request on open
  useEffect(() => {
    if (!open || !playerId) {
      setIsLoadingDefault(true);
      return;
    }

    setIsLoadingDefault(true);

    if (formRequestId) {
      FormServices.getFormRequests({ playerId, perPage: 100 })
        .then(result => {
          const req = result.content?.find(r => r.id === formRequestId);
          if (req) {
            setSelectedRequest(req);
            FormServices.getDefaultFormRequest({ playerId, formType: "review" })
              .then(defaultResp => {
                setDefaultRequest(defaultResp as any);
              })
              .catch(err => console.error("Error loading default request fallback", err));

            const formId = req.form?.id || (req as any)?.formId;
            if (formId) {
              setSelectedFormId(String(formId));
            }
          } else {
            toast.error("Review link not found");
          }
        })
        .catch(error => {
          console.error(error);
          toast.error("Error loading selected review link");
        })
        .finally(() => {
          setIsLoadingDefault(false);
        });
    } else {
      FormServices.getDefaultFormRequest({ playerId, formType: "review" })
        .then(response => {
          setSelectedRequest(response as any);
          setDefaultRequest(response as any);

          const formId = response?.form?.id || (response as any)?.formId;
          if (formId) {
            setSelectedFormId(String(formId));
          }
        })
        .catch(error => {
          console.error(error);
          toast.error("Error loading default review form");
        })
        .finally(() => {
          setIsLoadingDefault(false);
        });
    }
  }, [open, playerId, formRequestId]);

  useEffect(() => {
    if (!selectedFormId && formsList.length > 0) {
      setSelectedFormId(String(formsList[0].id));
    }
  }, [formsList, selectedFormId]);

  useEffect(() => {
    if (open) {
      if (initialShowCustom) {
        setShowCustomWorkflow(true);
      } else {
        setShowCustomWorkflow(false);
      }

      if (initialWizardStep) {
        setWizardStep(initialWizardStep);
      } else {
        setWizardStep(1);
      }

      if (initialFan) {
        const fanOpt = {
          id: initialFan.id,
          label: `${initialFan.displayName || "No Name"}${initialFan.email ? ` - ${initialFan.email}` : ""}`,
          original: initialFan,
        };
        setCreatedFan(fanOpt);
        fans.onValueChange(fanOpt as any);
      } else {
        setCreatedFan(null);
        fans.onValueChange(null as any);
      }
    } else {
      // Clear specific temporary states when closing
      setIsEditingRequest(false);
      setContextInstructions("");
      setUseAiPersonalization(false);
    }
  }, [open, initialShowCustom, initialFan?.id, initialWizardStep]);

  // Enable AI Personalization by default if a fan is selected and the form allows AI
  useEffect(() => {
    if (activeFanSelected && allowsAi && !isEditingRequest) {
      setUseAiPersonalization(true);
    }
  }, [activeFanSelected, allowsAi, isEditingRequest]);

  // 5. Handle creation of form default link or client-specific link
  const handleGenerateLink = async (): Promise<FormRequest | null> => {
    if (!selectedFormId) return null;

    setIsGenerating(true);
    try {
      const formId = Number(selectedFormId);
      const isCustomRequest = !!activeFanSelected || (allowsAi && useAiPersonalization);
      let requestResult: FormRequest;

      if (!isCustomRequest) {
        const requestsPage = await FormServices.getFormRequests({ playerId, formId, perPage: 10 });
        const existing = requestsPage.content.find(
          r => r.player?.id === playerId && !r.fan?.id && !r.useAiPersonalization
        );

        if (existing) {
          requestResult = existing;
          setSelectedRequest(existing);
        } else {
          const newRequest = await FormServices.createFormRequest(formId, { playerId });
          requestResult = newRequest;
          setSelectedRequest(newRequest);
        }
      } else {
        const customRequest = await FormServices.createFormRequest(formId, {
          playerId,
          fanId: activeFanSelected?.id || undefined,
          internalIdentifier: activeFanSelected
            ? activeFanSelected.original?.displayName || activeFanSelected.label.split(" - ")[0]
            : undefined,
          useAiPersonalization: allowsAi ? useAiPersonalization : false,
          contextInstructions: allowsAi && useAiPersonalization ? contextInstructions.trim() : undefined,
        });

        requestResult = customRequest;
        setSelectedRequest(customRequest);
        toast.success("Custom client review link generated!");
      }

      setShowCustomWorkflow(false);
      setCreatedFan(null);
      fans.onValueChange(null as any, undefined);
      setUseAiPersonalization(false);
      setContextInstructions("");
      return requestResult;
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate review link");
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpdateLink = async (): Promise<FormRequest | null> => {
    if (!selectedRequest) return null;

    setIsGenerating(true);
    try {
      const formId = selectedRequest.form?.id || (selectedRequest as any).formId || Number(selectedFormId);

      const updatedRequest = await FormServices.updateFormRequest(formId, selectedRequest.id, {
        playerId,
        fanId: activeFanSelected?.id && activeFanSelected.id !== -1 ? activeFanSelected.id : undefined,
        internalIdentifier: activeFanSelected
          ? activeFanSelected.original?.displayName || activeFanSelected.label.split(" - ")[0]
          : undefined,
        useAiPersonalization: allowsAi ? useAiPersonalization : false,
        contextInstructions: allowsAi && useAiPersonalization ? contextInstructions.trim() : undefined,
      });

      setSelectedRequest(updatedRequest);
      toast.success("Personalization details updated!");
      setShowCustomWorkflow(false);
      setIsEditingRequest(false);

      setCreatedFan(null);
      fans.onValueChange(null as any, undefined);
      setUseAiPersonalization(false);
      setContextInstructions("");
      return updatedRequest;
    } catch (error) {
      console.error(error);
      toast.error("Failed to update personalization details");
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  // 6. Handle Video Outreach Request Flow
  const handleOpenVideoRequestWorkflow = async () => {
    let activeRequest = selectedRequest;

    setIsGenerating(true);
    try {
      if (!activeRequest || isEditingRequest) {
        const createCall = isEditingRequest ? handleUpdateLink() : handleGenerateLink();
        const created = await createCall;
        if (created) {
          activeRequest = created;
        } else {
          return; // stop if link creation failed
        }
      }

      setIsGeneratingOutreachScript(true);
      setShowOutreachScriptDialog(true); // Open script modal

      const fanId = activeFanSelected?.id || activeRequest?.fan?.id || undefined;
      const scriptResult = await ReviewServices.generateReviewRequestScript({
        fanId,
        userInstructions: allowsAi && useAiPersonalization ? contextInstructions.trim() : undefined,
      });

      if (scriptResult?.script) {
        setOutreachScriptText(scriptResult.script);
      } else {
        toast.error("Failed to generate outreach script.");
        setShowOutreachScriptDialog(false);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error generating outreach script.");
      setShowOutreachScriptDialog(false);
    } finally {
      setIsGenerating(false);
      setIsGeneratingOutreachScript(false);
    }
  };

  const handleContinueToRecording = (script: string) => {
    setShowOutreachScriptDialog(false);
    setOutreachScriptText(script);
    setShowRecordDialog(true);
  };

  const handleVideoRecordingDone = (media: Media) => {
    setShowRecordDialog(false);
    setRecordedMedia(media);
    setShowVideoPageModal(true);
  };

  const handleQuickCreateFan = async (name: string) => {
    setIsGenerating(true);
    try {
      const newFan = await FanServices.createFan({
        displayName: name,
        playerId,
        active: true,
        fanType: "customer",
      });

      const fanOption = {
        id: newFan.id,
        label: `${newFan.displayName || "No Name"}${newFan.email ? ` - ${newFan.email}` : ""}`,
        original: newFan,
      };

      setCreatedFan(fanOption);
      toast.success("Fan added successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to quick-add fan contact.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEnterEditMode = () => {
    if (selectedRequest) {
      if (selectedRequest.fan) {
        const fanOpt = {
          id: selectedRequest.fan.id,
          label: `${selectedRequest.fan.displayName || "No Name"}${
            selectedRequest.fan.email ? ` - ${selectedRequest.fan.email}` : ""
          }`,
          original: selectedRequest.fan,
        };
        fans.onValueChange(fanOpt as any);
        setCreatedFan(fanOpt);
      } else if (selectedRequest.internalIdentifier) {
        const mockFanOpt = {
          id: -1,
          label: selectedRequest.internalIdentifier,
          original: { displayName: selectedRequest.internalIdentifier },
        };
        setCreatedFan(mockFanOpt);
      } else {
        fans.onValueChange(null as any);
        setCreatedFan(null);
      }

      setUseAiPersonalization(selectedRequest.useAiPersonalization);
      setContextInstructions(selectedRequest.contextInstructions || "");
      setIsEditingRequest(true);
      setShowCustomWorkflow(true);
      setWizardStep(3); // Start editing directly at step 3 (AI context) if editing
    }
  };

  const handleCancelCustomWorkflow = () => {
    setShowCustomWorkflow(false);
    setIsEditingRequest(false);
    setCreatedFan(null);
    fans.onValueChange(null as any, undefined);
    setUseAiPersonalization(false);
    setContextInstructions("");
    setWizardStep(1);
  };

  const handleResetToDefault = () => {
    if (defaultRequest) {
      setSelectedRequest(defaultRequest);
      if (defaultRequest.form) {
        setSelectedFormId(String(defaultRequest.form.id));
      }
      toast.info("Reset to standard review link");
    }
  };

  const handleFanCreated = async (formData: FanCreationSchemaType) => {
    setIsCreatingFan(true);
    const fanId =
      activeFanSelected?.id && !String(activeFanSelected.id).startsWith("temp-") ? activeFanSelected.id : undefined;

    try {
      const fanData = {
        ...formData,
        playerId,
        active: true,
      };

      const fan = fanId ? await FanServices.updateFan(fanId, fanData) : await FanServices.createFan(fanData);

      const fanOption = {
        id: fan.id,
        label: `${fan.displayName || "No Name"}${fan.email ? ` - ${fan.email}` : ""}`,
        original: fan,
      };

      setCreatedFan(fanOption);
      setIsCreateFanOpen(false);
      toast.success(fanId ? "Fan information updated!" : "Fan created and selected successfully!");
    } catch (err) {
      console.error("Failed to save fan", err);
      toast.error(fanId ? "Error updating fan contact" : "Error creating fan contact");
    } finally {
      setIsCreatingFan(false);
    }
  };

  const handleNextStepFromFanSelection = async (): Promise<boolean> => {
    if (activeFanSelected && typeof activeFanSelected.id === "string" && activeFanSelected.id.startsWith("temp-")) {
      setIsGenerating(true);
      try {
        const displayName = activeFanSelected.original?.displayName || activeFanSelected.label;
        const newFan = await FanServices.createFan({
          displayName,
          playerId,
          active: true,
          fanType: "customer",
        });

        const fanOption = {
          id: newFan.id,
          label: `${newFan.displayName || "No Name"}${newFan.email ? ` - ${newFan.email}` : ""}`,
          original: newFan,
        };

        setCreatedFan(fanOption);
        fans.onValueChange(fanOption as any, undefined);
        toast.success("Fan added successfully!");
        return true;
      } catch (err) {
        console.error("Failed to auto-create fan from combobox", err);
        toast.error("Failed to quick-add fan contact.");
        return false;
      } finally {
        setIsGenerating(false);
      }
    }
    return true;
  };

  const reviewLinkUrl = selectedRequest?.url || "";

  useEffect(() => {
    if (!open || !selectedRequest) return;
    const isPending = selectedRequest.status === "pending_ai";
    if (!isPending) return;

    let pollInterval: any;

    const poll = async () => {
      try {
        const result = await FormServices.getFormRequests({ perPage: 25 });
        const request = result.content?.find(r => r.id === selectedRequest.id);
        if (request && request.status === "ready") {
          setSelectedRequest(request);
          toast.success("AI Personalization complete! Review link is ready.");
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.error("Error polling form request", err);
      }
    };

    poll();
    pollInterval = setInterval(poll, 3000);

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [open, selectedRequest?.id, selectedRequest?.status]);

  const showLoader = isLoadingDefault || isGenerating;
  const isCustomLink = !!(
    selectedRequest &&
    (selectedRequest.internalIdentifier || selectedRequest.useAiPersonalization)
  );
  const isAiRunning = !!(selectedRequest && selectedRequest.status === "pending_ai");

  return (
    <ShareFormLinkContext.Provider
      value={{
        open,
        onOpenChange,
        playerId,
        formRequestId,
        initialShowCustom,
        isLoadingDefault,
        isGenerating,
        showLoader,
        isCustomLink,
        isAiRunning,
        reviewLinkUrl,
        qrLogoSrc,
        selectedRequest,
        setSelectedRequest,
        defaultRequest,
        selectedFormId,
        setSelectedFormId,
        selectedForm,
        allowsAi,
        formsList,
        isLoadingForms,
        formSelectItems,
        showCustomWorkflow,
        setShowCustomWorkflow,
        isEditingRequest,
        setIsEditingRequest,
        wizardStep,
        setWizardStep,
        createdFan,
        setCreatedFan,
        fans,
        activeFanSelected,
        isCreateFanOpen,
        initialFormId,
        setIsCreateFanOpen,
        isCreatingFan,
        isHelpOpen,
        setIsHelpOpen,
        useAiPersonalization,
        setUseAiPersonalization,
        contextInstructions,
        setContextInstructions,
        showOutreachScriptDialog,
        setShowOutreachScriptDialog,
        isGeneratingOutreachScript,
        outreachScriptText,
        setOutreachScriptText,
        showRecordDialog,
        setShowRecordDialog,
        recordedMedia,
        setRecordedMedia,
        showVideoPageModal,
        setShowVideoPageModal,
        handleGenerateLink,
        handleUpdateLink,
        handleOpenVideoRequestWorkflow,
        handleContinueToRecording,
        handleVideoRecordingDone,
        handleQuickCreateFan,
        handleEnterEditMode,
        handleCancelCustomWorkflow,
        handleResetToDefault,
        handleFanCreated,
        handleNextStepFromFanSelection,
      }}
    >
      {children}
    </ShareFormLinkContext.Provider>
  );
};
